import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  /** MAISON ADDED FOR IPHONE SUPPORT **/
   /** @type {MediaQueryList | null} */
  #customMql = null;
  /** @type {boolean | null} */
  #lastIsMobile = null;

  connectedCallback() {
    const { signal } = this.#controller;

    /* MAISON ADDED SUPPORT FOR CUSTOM BREAKPOINT */
    const customBreakpoint = parseInt(this.dataset.breakpoint, 10);
    if (!isNaN(customBreakpoint)) {
      this.#customMql = window.matchMedia(`(max-width: ${customBreakpoint - 0.02}px)`);
      this.#customMql.addEventListener('change', this.#handleMediaQueryChange, { signal });
    } else {
      mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
    }

    this.#lastIsMobile = this.#isMobile();
    this.#setDefaultOpenState();
    this.#applyBreakpointClass();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    /* MAISON ADDED SUPPORT FOR CUSTOM BREAKPOINT */
    const isMobile = this.#isMobile();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    /* MAISON ADDED SUPPORT GROUP ACCORDION */
    const group =
      this.dataset.group ||
      this.closest('.accordion')?.dataset.accordionGroup;

    if (group && !this.details.open) {
      document
        .querySelectorAll(`accordion-custom`)
        .forEach((accordion) => {
          const accordionGroup =
            accordion.dataset.group ||
            accordion.closest('.accordion')?.dataset.accordionGroup;

          if (accordion !== this && accordionGroup === group) {
            accordion.details.open = false;
          }
      });
    }
  };

  /**
   * Handles the media query change event.
   */

  /* MAISON FOR IPHONE SUPPORT */
  #handleMediaQueryChange = () => {
    const isMobile = this.#isMobile();

    // Only act on a real breakpoint crossing, never on scroll-induced noise
    if (isMobile === this.#lastIsMobile) return;
    this.#lastIsMobile = isMobile;

    this.#setDefaultOpenState();
    this.#applyBreakpointClass();
  };

  /* MAISON ADDED SUPPORT FOR CUSTOM BREAKPOINT */
  #isMobile() {
    if (this.#customMql) return this.#customMql.matches;
    return isMobileBreakpoint();
  }
  #applyBreakpointClass() {
    const isMobile = this.#isMobile();
    this.classList.toggle('is-mobile-custom', isMobile);
  }

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    /* MAISON ADDED SUPPORT FOR CUSTOM BREAKPOINT */
    const isMobile = this.#isMobile();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
